import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../auth";
import api from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import { Waves } from "lucide-react";

export function Login() {
  const { loginJWT, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await loginJWT(email, password, rememberMe);
      toast.success("Connecté !");
      nav(u.role === "mns" ? "/mns/dashboard" : u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de la connexion");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-12">
      <div className="card-soft p-8 fade-up">
        <div className="flex items-center gap-2 text-sky-700 mb-4"><Waves /> <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Connexion</h1></div>
        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div>
            <Label>Email</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
          </div>
          <div>
            <Label>Mot de passe</Label>
            <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none" data-testid="remember-me-label">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-4 h-4 accent-sky-700"
              data-testid="remember-me-checkbox"
            />
            <span>Rester connecté <span className="text-slate-400">(30 jours)</span></span>
          </label>
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-sky-700 hover:bg-sky-600" data-testid="login-submit">
            {loading ? "Connexion..." : "Se connecter"}
          </Button>
        </form>
        <div className="my-4 text-center text-xs uppercase tracking-widest text-slate-400">ou</div>
        <Button variant="outline" className="w-full rounded-full" onClick={() => loginWithGoogle("client")} data-testid="login-google">
          Continuer avec Google
        </Button>
        <p className="text-sm text-slate-600 mt-4 text-center">
          Pas de compte ? <Link to="/register" className="text-sky-700 font-medium">Inscrivez-vous</Link>
        </p>
      </div>
    </div>
  );
}

export function Register() {
  const { register, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    email: "", password: "", role: "client", phone: "",
    rgpd_accepted: false, marketing_consent: false,
    civility: "M", first_name: "", last_name: "", birth_date: "",
    referral_code: "",
  });
  const [loading, setLoading] = useState(false);
  const [refCheck, setRefCheck] = useState({ checked: false, valid: false, parrain_name: "" });

  // Pre-fill referral code from URL (?ref=CF-XXXX)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) setForm((f) => ({ ...f, referral_code: ref.toUpperCase() }));
  }, []);

  const checkRef = async (code) => {
    const v = (code || "").trim().toUpperCase();
    if (v.length < 4) { setRefCheck({ checked: false, valid: false, parrain_name: "" }); return; }
    try {
      const { data } = await api.post("/referral/check", { code: v });
      setRefCheck({ checked: true, valid: data.valid, parrain_name: data.parrain_name || "" });
    } catch { setRefCheck({ checked: true, valid: false, parrain_name: "" }); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rgpd_accepted) { toast.error("Vous devez accepter la politique de confidentialité"); return; }
    if (!form.first_name.trim() || !form.last_name.trim()) { toast.error("Nom et prénom obligatoires"); return; }
    if (form.referral_code && refCheck.checked && !refCheck.valid) {
      toast.error("Code parrainage invalide — corrigez-le ou laissez vide");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form, name: `${form.first_name.trim()} ${form.last_name.trim()}`,
        referral_code: form.referral_code ? form.referral_code.trim().toUpperCase() : undefined,
      };
      const u = await register(payload);
      toast.success(refCheck.valid ? `Compte créé ! +5€ à votre 1ère réservation (parrainage ${refCheck.parrain_name}).` : "Compte créé !");
      nav(u.role === "mns" ? "/mns/profile" : "/profile/client");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-12">
      <div className="card-soft p-8 fade-up">
        <div className="flex items-center gap-2 text-sky-700 mb-4"><Waves /> <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Créer un compte</h1></div>
        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div>
            <Label>Je suis</Label>
            <RadioGroup value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} className="grid grid-cols-2 gap-2 mt-1">
              <label className={`border rounded-xl p-3 flex items-center gap-2 cursor-pointer ${form.role === "client" ? "border-sky-600 bg-sky-50" : "border-slate-200"}`}>
                <RadioGroupItem value="client" data-testid="role-client" /> <span>Client</span>
              </label>
              <label className={`border rounded-xl p-3 flex items-center gap-2 cursor-pointer ${form.role === "mns" ? "border-sky-600 bg-sky-50" : "border-slate-200"}`}>
                <RadioGroupItem value="mns" data-testid="role-mns" /> <span>Maître-nageur</span>
              </label>
            </RadioGroup>
          </div>

          <div>
            <Label>Civilité</Label>
            <RadioGroup value={form.civility} onValueChange={(v) => setForm({ ...form, civility: v })} className="grid grid-cols-3 gap-2 mt-1">
              {[["M","Monsieur"],["Mme","Madame"],["Autre","Autre"]].map(([v,l]) => (
                <label key={v} className={`border rounded-xl p-2 text-sm flex items-center justify-center gap-2 cursor-pointer ${form.civility === v ? "border-sky-600 bg-sky-50" : "border-slate-200"}`}>
                  <RadioGroupItem value={v} data-testid={`civ-${v}`} /> <span>{l}</span>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Prénom <span className="text-red-500">*</span></Label><Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="reg-firstname" /></div>
            <div><Label>Nom <span className="text-red-500">*</span></Label><Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="reg-lastname" /></div>
          </div>
          <div>
            <Label>Date de naissance <span className="text-red-500">*</span></Label>
            <Input required type="date" max={new Date().toISOString().slice(0,10)} value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} data-testid="reg-birthdate" />
          </div>
          <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="reg-email" /></div>
          <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="reg-phone" /></div>
          <div><Label>Mot de passe</Label><Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="reg-password" /></div>

          <div>
            <Label>Code parrainage <span className="text-xs text-slate-400">(optionnel — +5€ pour vous et votre parrain)</span></Label>
            <Input
              value={form.referral_code}
              onChange={(e) => { const v = e.target.value.toUpperCase(); setForm({ ...form, referral_code: v }); checkRef(v); }}
              placeholder="Ex: CF-A2F9"
              className={refCheck.checked ? (refCheck.valid ? "border-emerald-400 focus:ring-emerald-300" : "border-red-400 focus:ring-red-300") : ""}
              data-testid="reg-referral"
            />
            {refCheck.checked && refCheck.valid && (
              <p className="text-xs text-emerald-700 mt-1" data-testid="ref-valid">✓ Parrainé par <strong>{refCheck.parrain_name}</strong> — 5€ crédités dès votre 1ère réservation.</p>
            )}
            {refCheck.checked && !refCheck.valid && form.referral_code && (
              <p className="text-xs text-red-600 mt-1" data-testid="ref-invalid">Code inconnu — vérifiez ou laissez vide.</p>
            )}
          </div>

          <label className="flex items-start gap-2 bg-sky-50 p-3 rounded-xl text-xs text-slate-700 cursor-pointer" data-testid="rgpd-label">
            <input type="checkbox" required checked={form.rgpd_accepted} onChange={(e) => setForm({ ...form, rgpd_accepted: e.target.checked })} className="mt-0.5" data-testid="rgpd-checkbox" />
            <span>En créant un compte, j'accepte que mes données soient utilisées pour fournir le service (réservations, messagerie, etc.). Vos données sont utilisées pour gérer vos réservations et vos échanges avec le coach.</span>
          </label>
          <label className="flex items-start gap-2 bg-slate-50 p-3 rounded-xl text-xs text-slate-700 cursor-pointer" data-testid="marketing-label">
            <input type="checkbox" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} className="mt-0.5" data-testid="marketing-checkbox" />
            <span>J'accepte de recevoir des emails à des fins marketing (newsletters, promotions, etc.). Vous pourrez vous désinscrire à tout moment depuis votre profil.</span>
          </label>

          <Button type="submit" disabled={loading} className="w-full rounded-full bg-sky-700 hover:bg-sky-600" data-testid="reg-submit">
            {loading ? "..." : "Créer mon compte"}
          </Button>
        </form>
        <div className="my-4 text-center text-xs uppercase tracking-widest text-slate-400">ou</div>
        <Button variant="outline" className="w-full rounded-full" onClick={() => loginWithGoogle(form.role)} data-testid="reg-google">
          Continuer avec Google
        </Button>
        <p className="text-sm text-slate-600 mt-4 text-center">
          Déjà inscrit ? <Link to="/login" className="text-sky-700 font-medium">Se connecter</Link>
        </p>
      </div>
    </div>
  );
}

export function AuthCallback() {
  const nav = useNavigate();
  const ref = React.useRef(false);
  React.useEffect(() => {
    if (ref.current) return;
    ref.current = true;
    const hash = window.location.hash;
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { nav("/login"); return; }
    const sid = m[1];
    const role = sessionStorage.getItem("oauth_role") || "client";
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/google/session`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sid, role }),
    }).then((r) => r.json()).then((d) => {
      // Cache CSRF in JS memory for cross-origin deployments
      if (d?.csrf_token) {
        try {
          // setCsrfToken is exported from api.js — import lazily to avoid a top-level cycle
          import("../api").then((m) => m.setCsrfToken(d.csrf_token)).catch((err) => {
            console.warn("[Auth] CSRF setup failed (non-blocking)", err);
          });
        } catch (e) {
          console.warn("[Auth] CSRF dynamic import failed (non-blocking)", e);
        }
      }
      window.location.replace(d.user?.role === "mns" ? "/mns/dashboard" : "/dashboard");
    }).catch(() => nav("/login"));
  }, [nav]);
  return <div className="p-12 text-center text-slate-500">Authentification...</div>;
}
